import React, { useEffect, useState, useMemo } from "react";
import "./ExpertCard.css";
import {
  hydrateExpertFromPreload,
  extractExpertId,
  getPreloadedExpertById,
} from "../Data/expert-pre-load-database";

// Extract tags with prioritization logic
const extractExpertiseTags = (expert = {}) => {
  const tags = expert.tags || [];
  const prioritized = [];
  const fallback = [];

  const params = new URLSearchParams(window.location.search);

  const keywords = [
    params.get("keyword_primary_1"),
    params.get("keyword_primary_2"),
    params.get("keyword_primary_3"),
    params.get("keyword_primary_4"),
    params.get("keyword_primary_5"),
    params.get("keyword_primary_6"),
    params.get("keyword_secondary_7"),
    params.get("keyword_secondary_1"),
    params.get("keyword_secondary_8"),
    params.get("keyword_secondary_9"),
    params.get("keyword_secondary_3"),
    params.get("keyword_secondary_6"),
  ].filter(Boolean);

  for (const tag of tags) {
    const name = tag.hashtag_name?.toLowerCase() || "";
    if (keywords.some((keyword) => name.includes(keyword))) {
      prioritized.push(tag.hashtag_name);
    } else {
      fallback.push(tag.hashtag_name);
    }
  }

  return prioritized.length ? prioritized : fallback.slice(0, 3);
};

// Async image validator
const checkImageExists = (url) => {
  return new Promise((resolve) => {
    if (!url) return resolve(false);
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
};

// Special ID check
const isKingsHawaiian = (expert) => {
  const id = expert?.global_id ?? expert?.globalId ?? expert?.id ?? null;
  return String(id) === "65718";
};

const ExpertCard = ({ expert = {}, loading, onTagClick }) => {
  // 1) HYDRATE from the in-bundle preload DB so this can render instantly.
  const hydratedExpert = useMemo(() => hydrateExpertFromPreload(expert), [expert]);

  // Determine if this expert is one of the preloaded VIPs (or an upstream shell).
  const preloadedForThisId = useMemo(() => {
    const id = extractExpertId(hydratedExpert);
    return !!getPreloadedExpertById(id) || !!expert.__preloaded;
  }, [hydratedExpert, expert]);

  // If preloaded, we start true and skip probing so the card shows immediately.
  const [hasValidImage, setHasValidImage] = useState(preloadedForThisId);

  const rawSendToIntercom = new URLSearchParams(window.location.search).get("send_to_intercom");
  const sendToIntercom = (rawSendToIntercom || "").toLowerCase() === "true";

  // 2) Validate whichever image we ended up with after hydration,
  // unless this is a preloaded VIP (then we skip).
  const imgUrl = hydratedExpert.profile_image_url;

  useEffect(() => {
    if (preloadedForThisId) {
      setHasValidImage(true);
      return;
    }
    let cancelled = false;
    (async () => {
      if (!imgUrl || imgUrl.includes("dicebear")) {
        if (!cancelled) setHasValidImage(false);
        return;
      }
      const ok = await checkImageExists(imgUrl);
      if (!cancelled) setHasValidImage(ok);
    })();
    return () => {
      cancelled = true;
    };
  }, [imgUrl, preloadedForThisId]);

  // Loading skeleton
  if (loading) {
    return (
      <div className="expert-card loading">
        <div className="card-header"></div>
        <div className="profile-section">
          <div className="profile-image loading-pulse"></div>
          <div className="profile-info">
            <h3 className="loading-pulse" style={{ width: "120px", height: "20px" }}></h3>
            <p className="title loading-pulse" style={{ width: "80px", height: "16px" }}></p>
          </div>
        </div>
        <div className="expert-on">
          <div className="tags">
            {[1, 2, 3].map((idx) => (
              <span key={idx} className="tag loading-pulse" style={{ width: "60px" }}></span>
            ))}
          </div>
        </div>
        <div className="latest-section">
          <p className="loading-pulse" style={{ width: "100%", height: "40px" }}></p>
        </div>
        <button className="contact-button loading-pulse"></button>
      </div>
    );
  }

  // Don’t render if no valid image
  if (!hasValidImage) return null;

  // Normal state (post-hydration)
  const tags = extractExpertiseTags(hydratedExpert);
  const name = hydratedExpert.name || "Unknown Expert";

  const rawTitle =
    hydratedExpert.featured_title_at_company ||
    hydratedExpert.headline ||
    hydratedExpert.featured_company_name ||
    "-";

  const displayTitle = isKingsHawaiian(hydratedExpert)
    ? "Spokesperson at King's Hawaiian, Celebrity Chef"
    : String(rawTitle).substring(0, 80);

  const description = hydratedExpert.description || "";

  const getProfileUrl = () => {
    try {
      if (!hydratedExpert.slug) return "https://app.qwoted.com/sources";
      return `https://app.qwoted.com/sources/${hydratedExpert.slug}`;
    } catch {
      return "https://app.qwoted.com/sources";
    }
  };

  const showFrequentSource = hydratedExpert.mediaCount > 5;
  const frequentSourceBadge =
    "https://qwotedstg.wpenginepowered.com/wp-content/uploads/2025/06/Frequent-Source.png";

  const handleTagClick = (tag) => {
    const formatted = tag.replace(/^#/, "").replace(/([A-Z])/g, " $1").trim();
    onTagClick && onTagClick(formatted);
  };

  return (
    <div className="expert-card">
      <div className="card-header"></div>
      <div className="profile-section">
        <div className="profile-image-wrapper">
          <img src={imgUrl} alt={name} className="profile-image" />
          {showFrequentSource && (
            <img src={frequentSourceBadge} alt="Frequent Source" className="frequent-source-badge" />
          )}
        </div>
        <div className="profile-info">
          <h3>{name}</h3>
          <p className="title">{displayTitle}</p>
        </div>
      </div>

      {tags.length > 0 && (
        <div className="expert-on">
          <div className="tags">
            {tags.map((tag, idx) => (
              <span key={idx} className="tag" onClick={() => handleTagClick(tag)}>
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {description && (
        <div className="latest-section">
          <p>{description.substring(0, 180)}...</p>
        </div>
      )}

      <button
        className="contact-button"
        onClick={() => {
          if (sendToIntercom) {
            const n = hydratedExpert.name || "Unknown Expert";
            const message = `Hi Shelby, I'd love to know more about booking ${n} for:`;
            window.parent.postMessage({ type: "OPEN_INTERCOM", message }, "https://www.qwoted.com");
          } else {
            window.open(getProfileUrl(), "_blank", "noopener,noreferrer");
          }
        }}
      >
        Contact
      </button>
    </div>
  );
};

export default ExpertCard;
